import { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * Custom hook to fetch a list of places.
 * 
 * @param {object} [filters] - Optional filters object to be passed as query parameters.
 *                             It is recommended to memoize this object (e.g., using `useMemo`)
 *                             if passed from a parent component to prevent unnecessary refetches,
 *                             as this hook uses it in its `useEffect` dependency array.
 * @returns {object} An object containing:
 *  - `places` (Array): The list of fetched places.
 *  - `loading` (boolean): Loading state.
 *  - `error` (object|null): Error object if fetching fails, otherwise null.
 */
const useFetchPlaces = (filters) => {
	const [places, setPlaces] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const fetchPlaces = async () => {
			try {
				const response = await api.get('/places', { params: filters });
				if (response.data && Array.isArray(response.data.places)) {
					setPlaces(response.data.places);
					setError(null);
				} else {
					setPlaces([]);
					setError({
						message: "Failed to fetch places. Please try again later.",
						originalError: new Error("Malformed response"),
					});
				}
			} catch (error) {
				setPlaces([]);
				setError({
					message: "Failed to fetch places. Please try again later.",
					originalError: error,
				});
			} finally {
				setLoading(false);
			}
		};

		fetchPlaces();
	}, [filters]);

	return { places, loading, error };
};

export default useFetchPlaces;
